import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Plus, ArrowLeft, Trash2, Download, DollarSign, X, Send, Eye, Ban, Printer } from "lucide-react";
import api, { formatErr, fileUrl, getToken } from "../lib/apiClient";
import { fmtCAD, fmtDate, statusClass } from "../lib/format";
import { LineItemsEditor } from "../components/LineItemsEditor";
import SendEmailDialog from "../components/SendEmailDialog";
import CustomerPicker from "../components/CustomerPicker";
import { ExportImportBar } from "../components/ExportImportBar";
import ConfirmDialog from "../components/ConfirmDialog";

/**
 * Item 7: shared helper — auth-aware PDF download/print. Fetches
 * /api/invoices/{id}/pdf with the Bearer token, then either offers the blob
 * as a download or opens it in a new tab and triggers window.print().
 */
async function invoicePdfAction(id, number, mode) {
  try {
    const token = getToken();
    const res = await fetch(fileUrl(`/invoices/${id}/pdf`), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    if (mode === "print") {
      const href = URL.createObjectURL(blob);
      const w = window.open(href, "_blank", "noopener,noreferrer");
      if (w) w.addEventListener("load", () => setTimeout(() => { try { w.print(); } catch (e) { /* user must trigger */ } }, 700));
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    } else {
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `invoice-${number || id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(href);
    }
  } catch (e) { alert(`PDF failed: ${e.message}`); }
}

const STATUS = ["Draft", "Sent", "Viewed", "Partially Paid", "Paid", "Overdue"];
const TERMS = ["C.O.D.", "Due on Receipt", "Net 15", "Net 30", "Net 60", "Custom"];

export function InvoicesList() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [customers, setCustomers] = useState({});
  const [params] = useSearchParams();
  const [selected, setSelected] = useState(new Set());
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchPay, setBatchPay] = useState(null); // null | { method, paid_date, notes }
  const [dlg, setDlg] = useState(null);

  useEffect(() => { const s = params.get("status"); if (s) setStatus(s); }, [params]);

  const load = async () => {
    const p = new URLSearchParams();
    if (status !== "all") p.set("status", status);
    const [{ data }, { data: custs }] = await Promise.all([api.get(`/invoices?${p}`), api.get("/customers")]);
    setItems(data);
    setCustomers(Object.fromEntries(custs.map((c) => [c.id, c])));
    setSelected(new Set());
  };
  useEffect(() => { load(); }, [status]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.job_id || i.id)));
  };

  const batchSend = () => {
    if (selected.size === 0) return;
    setDlg({
      title: `Send ${selected.size} invoice${selected.size > 1 ? "s" : ""} by email?`,
      body: "One email per invoice will be sent to each customer's saved email address.",
      confirmLabel: "Send",
      danger: false,
      onConfirm: async () => {
        setBatchBusy(true);
        try {
          const { data } = await api.post("/invoices/batch-send", { ids: Array.from(selected) });
          const errs = (data.errors || []).length;
          alert(`Sent ${(data.sent || []).length} invoices.${errs ? ` ${errs} failed (missing email or other error).` : ""}`);
          setSelected(new Set());
          await load();
        } catch (e) { alert(formatErr(e)); }
        finally { setBatchBusy(false); }
      },
    });
  };

  const batchDelete = () => {
    if (selected.size === 0) return;
    setDlg({
      title: `Delete ${selected.size} invoice${selected.size > 1 ? "s" : ""}?`,
      body: `This will soft-delete ${selected.size} invoice${selected.size > 1 ? "s" : ""}. Invoice numbers will be recycled for re-use.`,
      confirmLabel: "Delete",
      onConfirm: async () => {
        setBatchBusy(true);
        try {
          const { data } = await api.post("/invoices/batch-delete", { ids: Array.from(selected) });
          alert(`Deleted ${data.deleted}. ${data.numbers_freed} invoice number(s) freed for reuse.`);
          setSelected(new Set());
          await load();
        } catch (e) { alert(formatErr(e)); }
        finally { setBatchBusy(false); }
      },
    });
  };

  const submitBatchPay = async () => {
    setBatchBusy(true);
    try {
      const { data } = await api.post("/invoices/batch-payment", {
        ids: Array.from(selected),
        method: batchPay.method,
        paid_date: batchPay.paid_date,
        notes: batchPay.notes || "",
      });
      alert(`Recorded payments on ${data.count} invoice(s).`);
      setBatchPay(null);
      await load();
    } catch (e) { alert(formatErr(e)); }
    finally { setBatchBusy(false); }
  };

  const cancelJob = (e, jobId, number) => {
    e.stopPropagation();
    setDlg({
      title: `Cancel invoice ${number || jobId}?`,
      body: "The job stays in history but will be marked cancelled.",
      confirmLabel: "Cancel invoice",
      danger: false,
      onConfirm: async () => {
        try { await api.post(`/jobs/${jobId}/cancel`); await load(); }
        catch (err) { alert(formatErr(err)); }
      },
    });
  };
  const delJob = (e, jobId, number) => {
    e.stopPropagation();
    setDlg({
      title: `Delete invoice ${number || jobId}?`,
      body: "This permanently deletes the job and its invoice. This cannot be undone.",
      confirmLabel: "Delete",
      onConfirm: async () => {
        try { await api.delete(`/jobs/${jobId}`); await load(); }
        catch (err) { alert(formatErr(err)); }
      },
    });
  };

  return (
    <div data-testid="invoices-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="font-display text-3xl font-bold tracking-tight">Invoices</h1><p className="text-sm text-slate-500 mt-1">{items.length} invoice{items.length !== 1 ? "s" : ""}{selected.size > 0 && ` · ${selected.size} selected`}</p></div>
        <div className="flex items-center gap-2 self-start">
          <ExportImportBar entity="invoices" />
          <Link to="/invoices/new" data-testid="new-invoice-btn" className="px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2"><Plus size={16} /> New Invoice</Link>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap items-center">
        <select data-testid="invoice-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]">
          <option value="all">All invoices</option>
          <option value="open">Open (unpaid)</option>
          {STATUS.map((s) => <option key={s}>{s}</option>)}
        </select>
        {selected.size > 0 && (
          <div data-testid="invoice-batch-bar" className="flex items-center gap-2 px-3 py-1.5 bg-[#E0F2FE] border border-[#0F4C81]/30 rounded-md">
            <span className="text-xs font-medium text-[#0F4C81]">{selected.size} selected</span>
            <button onClick={batchSend} disabled={batchBusy} data-testid="batch-send-btn" className="px-2.5 py-1 text-xs bg-[#0F4C81] hover:bg-[#0A365C] text-white rounded flex items-center gap-1 disabled:opacity-50"><Send size={11}/>Send</button>
            <button onClick={() => setBatchPay({ method: "cash", paid_date: new Date().toISOString().split("T")[0], notes: "" })} disabled={batchBusy} data-testid="batch-pay-btn" className="px-2.5 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded flex items-center gap-1 disabled:opacity-50"><DollarSign size={11}/>Mark Paid</button>
            <button onClick={batchDelete} disabled={batchBusy} data-testid="batch-delete-btn" className="px-2.5 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded flex items-center gap-1 disabled:opacity-50"><Trash2 size={11}/>Delete</button>
            <button onClick={() => setSelected(new Set())} className="px-2.5 py-1 text-xs border border-slate-300 text-slate-700 rounded hover:bg-white">Clear</button>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 w-8"><input type="checkbox" data-testid="invoice-select-all" checked={items.length > 0 && selected.size === items.length} onChange={toggleAll}/></th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Number</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Customer</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium hidden sm:table-cell">Issued</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium hidden md:table-cell">Due</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Status</th>
                <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">Balance</th>
                <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">Total</th>
                <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500">No invoices yet.</td></tr>}
              {items.map((i) => {
                const bal = Math.max((i.total || 0) - (i.amount_paid || 0), 0);
                const rowId = i.job_id || i.id;
                return (
                  <tr key={i.id} data-testid={`invoice-row-${i.id}`} className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${selected.has(rowId) ? "bg-blue-50/40" : ""}`} onClick={() => window.location.href = `/jobs/${rowId}`}>
                    <td className="px-3 py-3" onClick={(e) => { e.stopPropagation(); toggle(rowId); }}>
                      <input type="checkbox" data-testid={`invoice-select-${i.id}`} checked={selected.has(rowId)} onChange={() => toggle(rowId)}/>
                    </td>
                    <td className="px-4 py-3 font-medium font-mono">
                      <div>{i.number}</div>
                      {i.job_number && i.job_number !== i.number && (
                        <div className="text-[10px] text-slate-400 font-normal">Job {i.job_number}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">{customers[i.customer_id]?.name || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 hidden sm:table-cell">{fmtDate(i.created_at)}</td>
                    <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{fmtDate(i.due_date)}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${statusClass(i.status)}`}>{i.status}</span></td>
                    <td className={`px-4 py-3 text-right font-medium tabular ${bal > 0 ? "text-slate-900" : "text-slate-400"}`}>{fmtCAD(bal)}</td>
                    <td className="px-4 py-3 text-right tabular">{fmtCAD(i.total)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); invoicePdfAction(i.id, i.number, "download"); }}
                          data-testid={`row-pdf-${i.id}`}
                          title="Download PDF"
                          className="p-1.5 text-slate-400 hover:text-[#0F4C81] hover:bg-[#0F4C81]/10 rounded"
                        ><Download size={13}/></button>
                        <button
                          onClick={(e) => { e.stopPropagation(); invoicePdfAction(i.id, i.number, "print"); }}
                          data-testid={`row-print-${i.id}`}
                          title="Print"
                          className="p-1.5 text-slate-400 hover:text-[#0F4C81] hover:bg-[#0F4C81]/10 rounded"
                        ><Printer size={13}/></button>
                        <button
                          onClick={(e) => cancelJob(e, i.job_id || i.id, i.number)}
                          data-testid={`cancel-invoice-${i.id}`}
                          title="Cancel job"
                          className="p-1.5 text-slate-400 hover:text-amber-700 hover:bg-amber-50 rounded"
                        ><Ban size={13}/></button>
                        <button
                          onClick={(e) => delJob(e, i.job_id || i.id, i.number)}
                          data-testid={`delete-invoice-${i.id}`}
                          title="Delete job + invoice"
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                        ><Trash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {batchPay && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setBatchPay(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="batch-pay-dialog" className="bg-white rounded-md max-w-sm w-full p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-display text-xl font-bold">Mark {selected.size} invoice(s) as paid</h3>
              <button onClick={() => setBatchPay(null)} className="p-1 hover:bg-slate-100 rounded"><X size={18}/></button>
            </div>
            <div className="text-xs text-slate-500">Each invoice's outstanding balance will be marked paid with the same method below.</div>
            <div>
              <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Method</label>
              <select data-testid="batch-pay-method" value={batchPay.method} onChange={(e) => setBatchPay({ ...batchPay, method: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md">
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
                <option value="e-transfer">E-Transfer</option>
                <option value="credit card">Credit Card</option>
                <option value="debit card">Debit Card</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Date</label>
              <input type="date" data-testid="batch-pay-date" value={batchPay.paid_date} onChange={(e) => setBatchPay({ ...batchPay, paid_date: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md"/>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Note (optional)</label>
              <input data-testid="batch-pay-notes" value={batchPay.notes} onChange={(e) => setBatchPay({ ...batchPay, notes: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md"/>
            </div>
            <button onClick={submitBatchPay} disabled={batchBusy} data-testid="batch-pay-submit" className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-md font-medium disabled:opacity-50">
              {batchBusy ? "Recording…" : "Record payments"}
            </button>
          </div>
        </div>
      )}
      <ConfirmDialog dialog={dlg} onClose={() => setDlg(null)} />
    </div>
  );
}

export function InvoiceForm() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [hstRate, setHstRate] = useState(13);

  // For existing invoices: hop straight to the unified Job page
  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    api.get(`/invoices/${id}`)
      .then(({ data }) => {
        if (cancelled) return;
        const jobId = data.job_id || data.id;
        nav(`/jobs/${jobId}`, { replace: true });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id, isNew, nav]);
  const [data, setData] = useState({
    customer_id: params.get("customer") || "",
    line_items: [],
    invoice_discount_type: "amount",
    invoice_discount_value: 0,
    payment_terms: "C.O.D.",
    notes: "",
    status: "Draft",
    hst_rate: null,
    hst_rate_label: "",
  });
  const [hstRates, setHstRates] = useState([]);
  const [existing, setExisting] = useState(null);
  const [err, setErr] = useState("");
  const [showPay, setShowPay] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [localDlg, setLocalDlg] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [emailHistory, setEmailHistory] = useState([]);
  const [editPay, setEditPay] = useState(null);
  const [pay, setPay] = useState({ amount: 0, method: "cash", reference: "", paid_date: new Date().toISOString().split("T")[0] });

  useEffect(() => {
    api.get("/customers").then(({ data }) => setCustomers(data));
    api.get("/settings").then(({ data }) => {
      setHstRate(data.hst_rate || 13);
      const rates = data.hst_rates || [];
      const hasDefault = rates.some((r) => Number(r.rate) === Number(data.hst_rate || 13));
      const list = hasDefault ? rates : [{ label: "Default", rate: Number(data.hst_rate || 13) }, ...rates];
      setHstRates(list);
      // Apply default payment terms from settings
      if (data.default_payment_terms) {
        setData((d) => d.payment_terms === "C.O.D." ? { ...d, payment_terms: data.default_payment_terms } : d);
      }
    });
    if (!isNew) {
      api.get(`/invoices/${id}`).then(({ data }) => { setData(data); setExisting(data); if (data.customer_id) api.get(`/customers/${data.customer_id}`).then(({ data: c }) => setCustomer(c)); });
      api.get(`/email-sends?entity_type=invoice&entity_id=${id}`).then(({ data }) => setEmailHistory(data));
    }
  }, [id]);

  const save = async () => {
    setErr("");
    try {
      const payload = { ...data, line_items: data.line_items, invoice_discount_value: Number(data.invoice_discount_value) };
      if (isNew) {
        const { data: inv } = await api.post("/invoices", payload);
        nav(`/jobs/${inv.job_id || inv.id}`);
      } else {
        await api.put(`/invoices/${id}`, payload);
        const { data: inv } = await api.get(`/invoices/${id}`);
        setData(inv); setExisting(inv);
        alert("Saved");
      }
    } catch (e) { setErr(formatErr(e)); }
  };

  const recordPayment = async () => {
    try {
      await api.post(`/invoices/${id}/payments`, { ...pay, amount: Number(pay.amount) });
      const { data: inv } = await api.get(`/invoices/${id}`);
      setData(inv); setExisting(inv);
      setShowPay(false);
      setPay({ amount: 0, method: "cash", reference: "", paid_date: new Date().toISOString().split("T")[0] });
    } catch (e) { alert(formatErr(e)); }
  };

  const del = () => {
    setLocalDlg({
      title: `Delete invoice ${existing?.number || ""}?`,
      body: "This permanently deletes the invoice. This cannot be undone.",
      confirmLabel: "Delete",
      onConfirm: async () => {
        try {
          await api.delete(`/invoices/${id}`);
          nav("/invoices");
        } catch (e) { alert(formatErr(e)); }
      },
    });
  };

  const balance = existing ? Math.max((existing.total || 0) - (existing.amount_paid || 0), 0) : 0;

  return (
    <div data-testid="invoice-form" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => nav(-1)} className="p-2 hover:bg-slate-100 rounded-md"><ArrowLeft size={18} /></button>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">{isNew ? "New Invoice" : existing?.number}</h1>
            {existing && <span className={`text-xs px-2 py-0.5 rounded ${statusClass(existing.status)}`}>{existing.status}</span>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!isNew && (
            <>
              <button
                type="button"
                onClick={() => invoicePdfAction(id, existing?.number, "download")}
                data-testid="invoice-pdf-btn"
                className="px-3 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50 flex items-center gap-2"
              >
                <Download size={14} /> Download PDF
              </button>
              <button
                type="button"
                onClick={() => invoicePdfAction(id, existing?.number, "print")}
                data-testid="invoice-print-btn"
                className="px-3 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50 flex items-center gap-2"
              >
                <Printer size={14} /> Print
              </button>
            </>
          )}
          {!isNew && <button onClick={() => nav(`/invoices/${id}/send`)} data-testid="send-invoice-email-btn" className="px-3 py-2 border border-[#0F4C81] text-[#0F4C81] text-sm rounded-md hover:bg-[#E0F2FE] flex items-center gap-2"><Send size={14} /> Send Email</button>}
          {!isNew && balance > 0 && <button onClick={() => { setPay({ ...pay, amount: balance }); setShowPay(true); }} data-testid="record-payment-btn" className="px-3 py-2 border border-emerald-300 text-emerald-700 text-sm rounded-md hover:bg-emerald-50 flex items-center gap-2"><DollarSign size={14} /> Record Payment</button>}
          {!isNew && <button onClick={del} className="p-2 text-slate-500 hover:text-red-600 border border-slate-300 rounded-md"><Trash2 size={16} /></button>}
          <button onClick={save} data-testid="save-invoice-btn" className="px-5 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium">Save</button>
        </div>
      </div>

      {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{err}</div>}

      {existing && existing.amount_paid > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 flex justify-between items-center">
          <div><div className="text-xs text-emerald-700 uppercase tracking-wider">Paid</div><div className="font-display text-2xl font-bold text-emerald-900 tabular">{fmtCAD(existing.amount_paid)}</div></div>
          <div className="text-right"><div className="text-xs text-emerald-700 uppercase tracking-wider">Balance</div><div className="font-display text-2xl font-bold text-emerald-900 tabular">{fmtCAD(balance)}</div></div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md p-6 grid sm:grid-cols-3 gap-4">
        <div className="sm:col-span-2">
          <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Customer *</label>
          <div className="mt-1">
            <CustomerPicker
              testid="invoice-customer"
              value={data.customer_id}
              onChange={(id) => setData({ ...data, customer_id: id })}
              required
              customers={customers}
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Payment Terms</label>
          <select data-testid="invoice-terms" value={data.payment_terms} onChange={(e) => setData({ ...data, payment_terms: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]">
            {TERMS.map((t) => <option key={t}>{t}</option>)}
          </select>
          {data.payment_terms === "Custom" && (
            <input
              type="date"
              data-testid="invoice-custom-due-date"
              value={data.due_date_override || ""}
              onChange={(e) => setData({ ...data, due_date_override: e.target.value })}
              className="mt-1.5 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]"
              placeholder="Due date"
            />
          )}
        </div>
        {hstRates.length > 1 && (
          <div>
            <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">HST Rate</label>
            <select
              data-testid="invoice-hst-rate"
              value={data.hst_rate ?? hstRate}
              onChange={(e) => {
                const val = Number(e.target.value);
                const r = hstRates.find((x) => Number(x.rate) === val);
                setData({ ...data, hst_rate: val, hst_rate_label: r?.label || "" });
              }}
              className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]"
            >
              {hstRates.map((r) => <option key={`${r.label}-${r.rate}`} value={r.rate}>{r.label} — {Number(r.rate).toFixed(2)}%</option>)}
            </select>
          </div>
        )}
        {existing && <div className="text-xs text-slate-500"><span className="block uppercase tracking-wider">Due Date</span><span className="text-sm text-slate-900 font-medium">{fmtDate(existing.due_date)}</span></div>}
      </div>

      <LineItemsEditor
        lineItems={data.line_items}
        setLineItems={(li) => setData({ ...data, line_items: li })}
        invDiscType={data.invoice_discount_type}
        setInvDiscType={(t) => setData({ ...data, invoice_discount_type: t })}
        invDiscVal={data.invoice_discount_value}
        setInvDiscVal={(v) => setData({ ...data, invoice_discount_value: v })}
        hstRate={hstRate}
      />

      <div className="bg-white border border-slate-200 rounded-md p-6">
        <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Notes</label>
        <textarea data-testid="invoice-notes" value={data.notes || ""} onChange={(e) => setData({ ...data, notes: e.target.value })} rows={5} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
      </div>

      {/* Per-invoice reminder controls */}
      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-3">
        <h3 className="font-display font-semibold text-sm flex items-center gap-2">Reminder schedule (this invoice)</h3>
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              data-testid="invoice-reminders-paused"
              checked={!!data.reminders_paused}
              onChange={(e) => setData({ ...data, reminders_paused: e.target.checked })}
            />
            Pause reminders for this invoice
            <span className="text-[11px] text-slate-400 ml-1">(e.g. customer promised a specific payment date)</span>
          </label>
          <div className={data.reminders_paused ? "opacity-40 pointer-events-none" : ""}>
            <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Interval override</label>
            <select
              data-testid="invoice-reminder-interval-override"
              value={data.reminder_interval_days_override || ""}
              onChange={(e) => setData({ ...data, reminder_interval_days_override: e.target.value ? parseInt(e.target.value) : null })}
              className="mt-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]"
            >
              <option value="">Use default (Settings)</option>
              <option value={1}>Every 1 day</option>
              <option value={3}>Every 3 days</option>
              <option value={7}>Every 7 days</option>
              <option value={14}>Every 14 days</option>
              <option value={30}>Every 30 days</option>
            </select>
          </div>
        </div>
        {existing?.reminders_sent_count > 0 && (
          <div className="text-[11px] text-slate-500">Sent {existing.reminders_sent_count} reminder(s) so far · last on {existing.last_reminder_at ? new Date(existing.last_reminder_at).toLocaleDateString("en-CA") : "—"}</div>
        )}
      </div>

      {existing?.payments?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <h3 className="font-display font-semibold mb-3">Payments</h3>
          <div className="space-y-2">
            {existing.payments.map((p) => (
              <div key={p.id} className="flex justify-between text-sm border-b border-slate-100 pb-2">
                <div><div className="font-medium tabular">{fmtCAD(p.amount)}</div><div className="text-xs text-slate-500">{p.method}{p.reference ? ` · ${p.reference}` : ""}</div></div>
                <div className="text-xs text-slate-500">{fmtDate(p.paid_date)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {emailHistory.length > 0 && (
        <div data-testid="invoice-email-history" className="bg-white border border-slate-200 rounded-md p-6">
          <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><Eye size={16} /> Email Activity</h3>
          <div className="space-y-2">
            {emailHistory.map((e) => (
              <div key={e.id} className="flex justify-between text-sm border-b border-slate-100 pb-2">
                <div>
                  <div className="text-sm">Sent to <span className="font-medium">{e.to}</span>{e.stubbed && <span className="text-xs ml-2 text-amber-600">(stubbed — no key)</span>}</div>
                  <div className="text-xs text-slate-500">{fmtDate(e.sent_at)} · {e.subject}</div>
                </div>
                <div className="text-right text-xs">
                  {e.opens > 0 ? <div className="text-emerald-700">Opened {e.opens}× · last {fmtDate(e.last_opened_at)}</div> : <div className="text-slate-500">Not opened yet</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showEmail && existing && (
        <SendEmailDialog entityType="invoice" entity={existing} customer={customer}
          onSent={() => api.get(`/email-sends?entity_type=invoice&entity_id=${id}`).then(({ data }) => setEmailHistory(data))}
          onClose={() => setShowEmail(false)} />
      )}

      {/* Recorded payments (item 2 + 3) */}
      {existing && (existing.payments || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <h3 className="font-display font-semibold mb-3">Payments received</h3>
          <ul data-testid="payments-list" className="divide-y divide-slate-100">
            {(existing.payments || []).map((p) => (
              <li key={p.id} data-testid={`payment-${p.id}`} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 tabular">{fmtCAD(p.amount)} · <span className="capitalize">{p.method}</span></div>
                  <div className="text-xs text-slate-500">Paid {fmtDate(p.paid_date)}{p.reference ? ` · ref ${p.reference}` : ""}{p.last_edited_at ? ` · last edited ${fmtDate(p.last_edited_at)}` : ""}</div>
                  {(p.audit_log || []).length > 0 && (
                    <details className="mt-1">
                      <summary className="text-[10px] text-slate-400 cursor-pointer hover:text-slate-700">View audit log ({p.audit_log.length})</summary>
                      <ul className="mt-1 ml-3 text-[11px] text-slate-500 space-y-0.5">
                        {p.audit_log.map((a, i) => (
                          <li key={i}>{a.at?.slice(0, 16).replace("T", " ")} — {a.by_name}: {a.from_method} → {a.to_method} ({a.reason})</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
                <button
                  data-testid={`edit-payment-${p.id}`}
                  onClick={() => setEditPay({
                    payment_id: p.id, original_method: p.method,
                    new_method: p.method, new_amount: p.amount,
                    new_paid_date: p.paid_date, reason: "",
                  })}
                  className="px-3 py-1.5 text-xs border border-slate-300 text-slate-700 rounded hover:bg-slate-50"
                >
                  Edit
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {editPay && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditPay(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="edit-payment-dialog" className="bg-white rounded-md max-w-sm w-full p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-display text-xl font-bold">Edit Payment</h3>
              <button onClick={() => setEditPay(null)} className="p-1 hover:bg-slate-100 rounded"><X size={18}/></button>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Method</label>
              <select data-testid="edit-payment-method" value={editPay.new_method} onChange={(e) => setEditPay({ ...editPay, new_method: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md">
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
                <option value="e-transfer">E-Transfer</option>
                <option value="credit card">Credit Card</option>
                <option value="debit card">Debit Card</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Reason for change *</label>
              <textarea
                data-testid="edit-payment-reason"
                value={editPay.reason}
                onChange={(e) => setEditPay({ ...editPay, reason: e.target.value })}
                rows={5}
                placeholder="e.g. Customer corrected — they actually paid by cheque, not cash"
                className="mt-1 w-full px-3 py-2 text-base border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81] resize-y"
              />
              <p className="text-[11px] text-slate-500 mt-1">Stored on the audit log alongside who/when changed it.</p>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                data-testid="save-payment-edit-btn"
                disabled={!editPay.reason.trim() || editPay.new_method === editPay.original_method}
                onClick={async () => {
                  try {
                    const jobId = existing.job_id || existing.id;
                    await api.post(`/jobs/${jobId}/payments/edit`, {
                      payment_id: editPay.payment_id,
                      new_method: editPay.new_method,
                      reason: editPay.reason.trim(),
                    });
                    const { data: fresh } = await api.get(`/invoices/${id}`);
                    setExisting(fresh);
                    setEditPay(null);
                  } catch (e) { alert(formatErr(e)); }
                }}
                className="flex-1 px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium disabled:opacity-40"
              >
                Save edit
              </button>
              <button onClick={() => setEditPay(null)} className="px-4 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showPay && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div data-testid="payment-dialog" className="bg-white rounded-md max-w-sm w-full p-6 space-y-4">
            <div className="flex justify-between items-center"><h3 className="font-display text-xl font-bold">Record Payment</h3><button onClick={() => setShowPay(false)} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button></div>
            <div className="text-sm text-slate-500">Balance due: <span className="font-semibold text-slate-900 tabular">{fmtCAD(balance)}</span></div>
            <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Amount</label><input data-testid="payment-amount" type="number" step="0.01" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md tabular focus:outline-none focus:border-[#0F4C81]" /></div>
            <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Method</label>
              <select data-testid="payment-method" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]">
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
                <option value="e-transfer">E-Transfer</option>
                <option value="credit card">Credit Card</option>
                <option value="debit card">Debit Card</option>
              </select>
            </div>
            <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Reference (optional)</label><input data-testid="payment-reference" value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" /></div>
            <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Date</label><input type="date" value={pay.paid_date} onChange={(e) => setPay({ ...pay, paid_date: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" /></div>
            <div className="flex gap-2 pt-2">
              <button onClick={recordPayment} data-testid="save-payment-btn" className="flex-1 px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium">Save Payment</button>
              <button onClick={() => setShowPay(false)} className="px-4 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50">Cancel</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog dialog={localDlg} onClose={() => setLocalDlg(null)} />
    </div>
  );
}
